import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { MaterialRequestsService, errMsg } from '../logistics.service';

/** Un origen posible para un código: un locker de hospital o un FSL (SAP). */
interface SourceRow {
  key: string;
  sourceType: 'HOSPITAL' | 'FSL';
  own: boolean;              // in-house del mismo hospital que pide
  place?: string; warehouse?: string | null; plant?: string;
  origin: string;            // alias del hospital o nombre del FSL
  sub: string;               // locker / planta
  available: number;          // piezas en este locker / FSL (tope del renglón)
  groupAvailable: number;     // stock disponible de todo el hospital (o del FSL)
  consumption: number | null; // del hospital (90 días); FSL no aplica
  minStock: number | null; suggestedStock: number | null;
  overstock: number;          // del hospital (o del FSL): lo único que se puede surtir
  group: string;              // hospital o FSL al que pertenece el renglón
  firstOfPlace: boolean; placeRows: number;
  qty: number;               // a surtir de aquí
}
interface LineBlock { product: string; code: string; description: string; missing: number; quantity: number; rows: SourceRow[]; }

/**
 * Surtir una solicitud (mesa de control I y II, administradores): muestra en
 * todo el proyecto dónde hay cada código — el in-house del mismo hospital,
 * otros hospitales y sus lockers por el
 * inventario actual, con su consumo de los últimos 90 días, stock mínimo,
 * sugerido y sobrestock (lo único que se puede surtir), y FSL por el último corte de SAP — y registra de dónde se surte.
 * Ya no se hace el traspaso desde aquí.
 */
@Component({
  selector: 'app-supply-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule],
  styleUrl: '../logistics-dialog.scss',
  styles: [`
    .wrap { display: flex; flex-direction: column; gap: 14px; min-width: min(860px, 88vw); }
    .meta { display: flex; flex-wrap: wrap; gap: 6px 16px; font-size: 12.5px; color: #555; }
    .block { border: 1px solid #DCDCDC; border-radius: 10px; overflow: hidden; }
    .block__head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; padding: 10px 12px; background: #F4F6FD; }
    .block__head b { color: #170F5F; }
    .block__head .desc { color: #555; font-size: 13px; }
    .block__head .spacer { flex: 1; }
    .block__head .tally { font-size: 13px; font-weight: 600; }
    .tally.ok { color: #14853d; } .tally.over { color: #c62828; } .tally.part { color: #9a6b00; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th { text-align: left; font-weight: 600; color: #555; padding: 6px 10px; border-bottom: 1px solid #E6E6E6; white-space: nowrap; }
    td { padding: 6px 10px; border-bottom: 1px solid #F0F0F0; vertical-align: middle; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    .sub { display: block; font-size: 12px; color: #777; }
    .kind { font-size: 11.5px; font-weight: 700; padding: 2px 7px; border-radius: 6px; background: #EEF3FF; color: #1010EB; }
    .kind--FSL { background: #EDFCF3; color: #0D5929; }
    .kind--OWN { background: #FFF4E0; color: #8A4B00; }
    .spare { font-weight: 600; color: #14853d; } .spare--none { color: #9e9e9e; font-weight: 400; }
    input.qty { width: 72px; height: 32px; box-sizing: border-box; border: 1px solid #BFBFBF; border-radius: 6px; padding: 0 8px;
      font: inherit; text-align: right; }
    input.qty:focus { outline: 2px solid #1010EB; outline-offset: 1px; }
    .empty { color: #777; padding: 12px; }
  `],
  template: `
<h2 mat-dialog-title>Surtir {{ data.request.folio }} · {{ placeAlias }}</h2>
<mat-dialog-content>
  <div class="wrap">
    @if (loading()) {
      <div class="counter"><mat-spinner diameter="22"></mat-spinner> Buscando dónde hay el material…</div>
    } @else if (error) {
      <div class="hint-box hint-box--warn"><mat-icon>error</mat-icon><span>{{ error }}</span></div>
    } @else {
      <div class="meta">
        <span><b>Hospitales y lockers:</b> inventario actual (del mismo hospital, solo su in-house)</span>
        <span><b>Stock mínimo:</b> del contrato del hospital</span>
        <span><b>Stock sugerido:</b> {{ suggestedDays }} días del consumo del almacén en folios y remisiones (nunca menor al mínimo)</span>
        <span><b>Sobrestock:</b> por almacén, stock disponible − stock sugerido; es lo único que se puede surtir</span>
        <span><b>FSL:</b> {{ sap ? ('corte SAP ' + sap.snapshotDate + ' · ' + sap.fileName) : 'sin corte de SAP cargado' }}</span>
      </div>

      @for (b of blocks; track b.product) {
        <section class="block">
          <div class="block__head">
            <b>{{ b.code }}</b><span class="desc">{{ b.description }}</span>
            <span class="spacer"></span>
            <span class="tally" [class]="'tally ' + tallyClass(b)">{{ assigned(b) }} de {{ b.missing }} asignadas</span>
          </div>
          @if (b.rows.length) {
            <table>
              <thead><tr>
                <th>Origen</th><th>Tipo</th><th class="num">Stock disponible</th>
                <th class="num">Stock mínimo</th><th class="num">Stock sugerido</th>
                <th class="num">Sobrestock</th><th class="num">Surtir</th>
              </tr></thead>
              <tbody>
                @for (r of b.rows; track r.key) {
                  <tr>
                    <td>{{ r.origin }}<span class="sub">{{ r.sub }}</span></td>
                    <td><span [class]="'kind kind--' + (r.own ? 'OWN' : r.sourceType)">{{ r.sourceType === 'FSL' ? 'FSL' : r.own ? 'In-house propio' : 'Hospital' }}</span></td>
                    <td class="num">{{ r.available }}</td>
                    <td class="num">{{ r.minStock ?? '—' }}</td>
                    <td class="num">{{ r.suggestedStock ?? '—' }}</td>
                    <td class="num"><span [class]="r.overstock ? 'spare' : 'spare spare--none'">{{ r.overstock }}</span></td>
                    <td class="num"><input class="qty" type="number" min="0" [max]="cap(b, r)" step="1"
                      [value]="r.qty" (input)="setQty(b, r, $any($event.target))" [disabled]="!r.qty && !cap(b, r)"
                      [matTooltip]="!r.qty && !cap(b, r) ? (r.overstock ? 'Ya está asignado todo lo que falta' : 'Sin sobrestock') : ''"
                      [attr.aria-label]="'Piezas a surtir de ' + r.origin + ' ' + r.sub"></td>
                  </tr>
                }
              </tbody>
            </table>
          } @else {
            <p class="empty">Ningún hospital ni FSL tiene este código disponible. Pídelo a Macrocentro.</p>
          }
        </section>
      }
      @if (!blocks.length) {
        <div class="hint-box"><mat-icon>check_circle</mat-icon><span>Esta solicitud ya no tiene piezas pendientes.</span></div>
      }
      <div class="hint-box"><mat-icon>info</mat-icon>
        <span>Al registrar el surtido la solicitud pasa a <b>En surtido</b>. Ya no se hace el traspaso desde aquí; el envío al Forms y por correo se activará cuando estén listos.</span></div>
    }
  </div>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-button (click)="ref.close(false)">Cerrar</button>
  @if (!loading() && !error && blocks.length) {
    <button mat-flat-button color="primary" (click)="save()" [disabled]="saving() || !canSave">
      <mat-icon>task_alt</mat-icon> Registrar surtido</button>
  }
</mat-dialog-actions>
  `,
})
export class SupplyDialogComponent implements OnInit {
  readonly data = inject<{ request: any }>(MAT_DIALOG_DATA);
  readonly ref = inject(MatDialogRef<SupplyDialogComponent>);
  private readonly service = inject(MaterialRequestsService);
  private readonly msg = inject(CustomMessageService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  error = '';
  placeAlias = '';
  days = 90;
  suggestedDays = 30;
  sap: { snapshotDate: string; fileName: string } | null = null;
  blocks: LineBlock[] = [];

  async ngOnInit(): Promise<void> {
    try {
      const a = await this.service.availability(this.data.request.id);
      this.placeAlias = a?.request?.placeAlias ?? '';
      this.days = a?.consumptionDays ?? 90;
      this.suggestedDays = a?.suggestedDays ?? 30;
      this.sap = a?.sap ?? null;
      const plan: any[] = a?.request?.supplyPlan ?? [];
      const planned = (p: string, kind: string, key: string) => plan
        .filter(x => x.product === p && x.sourceType === kind && (kind === 'FSL' ? (x.plant || x.label) === key : (x.warehouse || '') === key))
        .reduce((s, x) => s + (Number(x.quantity) || 0), 0);

      this.blocks = ((a?.lines ?? []) as any[]).filter(l => l.missing > 0 && l.product).map(l => {
        const rows: SourceRow[] = [];
        for (const h of (a.hospitals ?? []) as any[]) {
          const ws = (h.warehouses as any[]).filter(w => (w.counts?.[l.product] ?? 0) > 0);
          ws.forEach((w, i) => rows.push({
            key: 'H' + h.place + (w.warehouse ?? ''), sourceType: 'HOSPITAL', own: !!h.own, place: h.place, warehouse: w.warehouse,
            origin: h.alias, sub: `${w.name}${h.own ? ' (in-house)' : ''}`, available: w.counts[l.product],
            groupAvailable: w.counts[l.product],
            consumption: w.consumption?.[l.product] ?? 0,
            minStock: w.minStock?.[l.product] ?? 0, suggestedStock: w.suggestedStock?.[l.product] ?? 0,
            overstock: w.overstock?.[l.product] ?? 0, group: 'W' + h.place + (w.warehouse ?? ''),
            firstOfPlace: i === 0, placeRows: ws.length, qty: planned(l.product, 'HOSPITAL', w.warehouse ?? ''),
          }));
        }
        for (const f of (a.fsl ?? []) as any[]) {
          const n = f.counts?.[l.product] ?? 0;
          if (n > 0) rows.push({
            key: 'F' + (f.plant || f.label), sourceType: 'FSL', own: false, plant: f.plant, origin: f.label, sub: f.plant ? 'Planta ' + f.plant : 'FSL',
            available: n, groupAvailable: n, consumption: null, minStock: null, suggestedStock: null, overstock: n,
            group: 'F' + (f.plant || f.label), firstOfPlace: true, placeRows: 1,
            qty: planned(l.product, 'FSL', f.plant || f.label),
          });
        }
        return { product: l.product, code: l.code, description: l.description, missing: l.missing, quantity: l.quantity, rows };
      });
    } catch (err) {
      this.error = errMsg(err, 'No se pudo consultar la disponibilidad.');
    } finally {
      this.loading.set(false);
    }
  }

  /** Máximo para este renglón: su stock, el sobrestock que le queda a su
   *  hospital / FSL y lo que falta del código. Asignado todo, ya no deja más. */
  cap(b: LineBlock, r: SourceRow): number {
    const inGroup = b.rows.filter(x => x.group === r.group).reduce((s, x) => s + x.qty, 0) - r.qty;
    const others = this.assigned(b) - r.qty;
    return Math.max(0, Math.min(r.available, r.overstock - inGroup, b.missing - others));
  }

  setQty(b: LineBlock, r: SourceRow, el: HTMLInputElement): void {
    const v = Math.max(0, Math.min(Math.floor(Number(el.value) || 0), this.cap(b, r)));
    r.qty = v;
    el.value = String(v);
  }

  assigned(b: LineBlock): number { return b.rows.reduce((s, r) => s + Math.max(0, Math.floor(Number(r.qty) || 0)), 0); }
  tallyClass(b: LineBlock): string {
    const n = this.assigned(b);
    return n > b.missing ? 'over' : n === b.missing ? 'ok' : n > 0 ? 'part' : '';
  }

  get canSave(): boolean {
    const any = this.blocks.some(b => this.assigned(b) > 0);
    const valid = this.blocks.every(b => this.assigned(b) <= b.missing && b.rows.every(r => r.qty >= 0 && r.qty <= r.available)
      && b.rows.every(r => b.rows.filter(x => x.group === r.group).reduce((s, x) => s + x.qty, 0) <= r.overstock));
    return any && valid;
  }

  async save(): Promise<void> {
    if (!this.canSave) return;
    const plan = this.blocks.flatMap(b => b.rows.filter(r => Math.floor(Number(r.qty) || 0) > 0).map(r => ({
      product: b.product, sourceType: r.sourceType, quantity: Math.floor(Number(r.qty)),
      ...(r.sourceType === 'HOSPITAL' ? { place: r.place, warehouse: r.warehouse ?? null } : { plant: r.plant ?? '', label: r.origin }),
    })));
    this.saving.set(true);
    try {
      await this.service.supply(this.data.request.id, plan);
      await this.msg.open({ title: '¡Surtido registrado!', type: 'success', duration: 2600,
        message: `La solicitud <b>${this.data.request.folio}</b> quedó En surtido.` });
      this.ref.close(true);
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo registrar el surtido.'), dismissBtn: true });
    } finally {
      this.saving.set(false);
    }
  }
}
